import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Button, Card, Input, useToast } from '@caruso/ui';
import { Save, Settings as SettingsIcon, Eye, EyeOff, CheckCircle, XCircle, Plug, ChevronRight } from 'lucide-react';
import { settingsService } from '@/services/settings.service';
import { aiService } from '@/services/ai.service';

export function SettingsPage() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [testLoading, setTestLoading] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [openaiApiKey, setOpenaiApiKey] = useState('');
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const apiKeySetting = await settingsService.getByKey('openai_api_key');
      if (apiKeySetting) {
        setOpenaiApiKey(apiKeySetting.value || '');
      }
    } catch (error) {
      // Configuração ainda não existe, é normal
      console.log('API Key not configured yet');
    }
  };

  const handleTestConnection = async () => {
    try {
      setTestLoading(true);
      setTestResult(null);

      const result = await aiService.testConnection();
      setTestResult(result);

      toast({
        title: result.success ? 'Conexão estabelecida!' : 'Falha na conexão',
        description: result.message,
        variant: result.success ? 'success' : 'destructive',
      });
    } catch (error: any) {
      setTestResult({
        success: false,
        message: error.response?.data?.message || 'Erro ao testar conexão',
      });
      toast({
        title: 'Erro ao testar conexão',
        description: 'Verifique se você salvou a API Key primeiro',
        variant: 'destructive',
      });
    } finally {
      setTestLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setTestResult(null);

      if (!openaiApiKey) {
        toast({
          title: 'Preencha a API Key',
          variant: 'destructive',
        });
        return;
      }

      try {
        // Tentar atualizar
        await settingsService.update('openai_api_key', {
          value: openaiApiKey,
          description: 'OpenAI API Key para geração de conteúdo com IA',
        });
      } catch (error) {
        // Se não existir, criar
        await settingsService.create({
          key: 'openai_api_key',
          value: openaiApiKey,
          description: 'OpenAI API Key para geração de conteúdo com IA',
          encrypted: true,
        });
      }

      toast({
        title: 'Configurações salvas',
        description: 'Agora teste a conexão para validar',
        variant: 'success',
      });
    } catch (error) {
      toast({
        title: 'Erro ao salvar configurações',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-serif text-brand-gold">Configurações do Sistema</h1>
        <p className="text-brand-ivory/60 mt-1">
          Configure as integrações e preferências do sistema
        </p>
      </div>

      {/* OpenAI Settings */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <Card className="bg-brand-ink/50 border-brand-gold/20 p-6 space-y-6">
          <div className="flex items-center gap-3 pb-4 border-b border-brand-gold/20">
            <SettingsIcon className="h-6 w-6 text-brand-gold" />
            <div>
              <h2 className="text-xl font-serif text-brand-gold">Integração OpenAI</h2>
              <p className="text-sm text-brand-ivory/60">
                Configure a API Key para usar a geração de conteúdo com IA
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-brand-ivory/80 mb-2">
                OpenAI API Key *
              </label>
              <div className="relative">
                <Input
                  type={showApiKey ? 'text' : 'password'}
                  value={openaiApiKey}
                  onChange={(e) => setOpenaiApiKey(e.target.value)}
                  placeholder="sk-..."
                  className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory pr-12"
                />
                <button
                  type="button"
                  onClick={() => setShowApiKey(!showApiKey)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-ivory/50 hover:text-brand-gold transition-colors"
                >
                  {showApiKey ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
              <p className="text-xs text-brand-ivory/50 mt-1">
                Obtenha sua API Key em{' '}
                <a
                  href="https://platform.openai.com/api-keys"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-brand-gold hover:underline"
                >
                  platform.openai.com/api-keys
                </a>
              </p>
            </div>

            <div className="p-4 bg-brand-gold/10 border border-brand-gold/20 rounded-lg">
              <p className="text-sm text-brand-ivory/80">
                <strong className="text-brand-gold">Nota de Segurança:</strong> Sua API Key
                será armazenada de forma criptografada no banco de dados.
              </p>
            </div>

            {/* Test Result */}
            {testResult && (
              <div
                className={`p-4 rounded-lg border ${
                  testResult.success
                    ? 'bg-green-500/10 border-green-500/30'
                    : 'bg-red-500/10 border-red-500/30'
                }`}
              >
                <div className="flex items-start gap-3">
                  {testResult.success ? (
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                  )}
                  <div className="flex-1">
                    <p
                      className={`text-sm ${
                        testResult.success ? 'text-green-100' : 'text-red-100'
                      }`}
                      style={{ whiteSpace: 'pre-line' }}
                    >
                      {testResult.message}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="flex gap-3 pt-4 border-t border-brand-gold/20">
            <Button
              onClick={handleTestConnection}
              variant="outline"
              disabled={testLoading || loading}
              className="border-brand-gold/40 text-brand-gold hover:bg-brand-gold/10"
            >
              {testLoading ? 'Testando...' : 'Testar Conexão'}
            </Button>
            <Button onClick={handleSave} variant="premium" disabled={loading || testLoading} className="ml-auto">
              <Save className="w-4 h-4 mr-2" />
              {loading ? 'Salvando...' : 'Salvar Configurações'}
            </Button>
          </div>
        </Card>
      </motion.div>

      {/* Integrations Section */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
      >
        <Card
          className="bg-brand-ink/50 border-brand-gold/20 p-6 cursor-pointer hover:border-brand-gold/40 transition-colors"
          onClick={() => navigate('/app/settings/integracoes')}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Plug className="h-6 w-6 text-brand-gold" />
              <div>
                <h2 className="text-xl font-serif text-brand-gold">Integrações</h2>
                <p className="text-sm text-brand-ivory/60">
                  Configure WhatsApp (Evolution API), Email (SendGrid) e Pagamentos (ASAAS)
                </p>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-brand-gold/50" />
          </div>
        </Card>
      </motion.div>
    </div>
  );
}
